// ===========================================
// Payment Settings Tab
// Payment Processing Fee Configuration
// ===========================================

import { useState, useEffect, useMemo } from 'react';
import { usePermissions } from '../../../hooks/usePermissions';
import { useForm, Controller } from 'react-hook-form';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  RadioGroup,
  Radio,
  TextField,
  Select,
  MenuItem,
  InputAdornment,
  Button,
  Stack,
  Typography,
  Alert,
  Switch,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Save as SaveIcon,
  Refresh as ResetIcon,
} from '@mui/icons-material';
import {
  usePaymentSettings,
  usePaymentRates,
  useUpdatePaymentSettings,
  useResetPaymentSettings,
} from '../../../hooks/useAdminSettings';
import type { FeeMode, StripeProcessingRates } from '../../../types/adminSettings.types';

// ===========================================
// FALLBACK RATES (used only while API loads)
// ===========================================

const FALLBACK_RATES: StripeProcessingRates = {
  AU: { percentRate: 1.75, fixedCents: 30, currency: 'AUD', label: 'Australia' },
};

// ===========================================
// TYPES
// ===========================================

interface PaymentSettingsFormData {
  feeMode: FeeMode;
  countryCode: string;
  useCustomRate: boolean;
  customPercentRate: number;
  customFixedCents: number;
}

// ===========================================
// MAIN COMPONENT
// ===========================================

export default function PaymentSettingsTab() {
  const { can } = usePermissions();
  // Roles slice 1a (V7b): the tab shows with operations_payments.read; saving needs system_settings.write.
  const canSave = can('system_settings', 'write');
  const { data: settings, isLoading, error } = usePaymentSettings();
  const { data: rates } = usePaymentRates();
  const updateMutation = useUpdatePaymentSettings();
  const resetMutation = useResetPaymentSettings();
  const [resetDialogOpen, setResetDialogOpen] = useState(false);

  // Use API rates when available, fall back to minimal default
  const processingRates = rates ?? FALLBACK_RATES;

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { isDirty },
  } = useForm<PaymentSettingsFormData>({
    defaultValues: {
      feeMode: 'ABSORB',
      countryCode: 'AU',
      useCustomRate: false,
      customPercentRate: 1.75,
      customFixedCents: 30,
    },
  });

  // Watch form values for conditional rendering
  const feeMode = watch('feeMode');
  const countryCode = watch('countryCode');
  const useCustomRate = watch('useCustomRate');
  const customPercentRate = watch('customPercentRate');
  const customFixedCents = watch('customFixedCents');

  // Reset form when settings load
  useEffect(() => {
    if (settings) {
      const countryRate = processingRates[settings.countryCode] || processingRates.AU;
      reset({
        feeMode: settings.feeMode,
        countryCode: settings.countryCode,
        useCustomRate: settings.customRate !== null,
        customPercentRate: settings.customRate?.percentRate ?? countryRate?.percentRate ?? 1.75,
        customFixedCents: settings.customRate?.fixedCents ?? countryRate?.fixedCents ?? 30,
      });
    }
  }, [settings, processingRates, reset]);

  // Calculate preview fee
  const preview = useMemo(() => {
    const baseAmountCents = 10000; // $100.00
    const countryRate = processingRates[countryCode] || processingRates.AU;
    const percentRate = useCustomRate ? customPercentRate : (countryRate?.percentRate ?? 0);
    const fixedCents = useCustomRate ? customFixedCents : (countryRate?.fixedCents ?? 0);

    if (feeMode === 'ABSORB') {
      return { base: 100, fee: 0, total: 100 };
    }

    const feeCents = Math.round(baseAmountCents * (percentRate / 100) + fixedCents);
    return {
      base: 100,
      fee: feeCents / 100,
      total: (baseAmountCents + feeCents) / 100,
    };
  }, [feeMode, countryCode, useCustomRate, customPercentRate, customFixedCents, processingRates]);

  const onSubmit = (formData: PaymentSettingsFormData) => {
    const payload = {
      feeMode: formData.feeMode,
      countryCode: formData.countryCode,
      customRate: formData.useCustomRate
        ? { percentRate: formData.customPercentRate, fixedCents: formData.customFixedCents }
        : null,
    };
    updateMutation.mutate(payload, {
      onSuccess: (data) => {
        const countryRate = processingRates[data.countryCode] || processingRates.AU;
        reset({
          feeMode: data.feeMode,
          countryCode: data.countryCode,
          useCustomRate: data.customRate !== null,
          customPercentRate: data.customRate?.percentRate ?? countryRate?.percentRate ?? 1.75,
          customFixedCents: data.customRate?.fixedCents ?? countryRate?.fixedCents ?? 30,
        });
      },
    });
  };

  const handleResetConfirm = () => {
    setResetDialogOpen(false);
    resetMutation.mutate(undefined, {
      onSuccess: (data) => {
        const countryRate = processingRates[data.countryCode] || processingRates.AU;
        reset({
          feeMode: data.feeMode,
          countryCode: data.countryCode,
          useCustomRate: data.customRate !== null,
          customPercentRate: data.customRate?.percentRate ?? countryRate?.percentRate ?? 1.75,
          customFixedCents: data.customRate?.fixedCents ?? countryRate?.fixedCents ?? 30,
        });
      },
    });
  };

  if (error) {
    return (
      <Alert severity="error">
        Failed to load payment settings: {error.message}
      </Alert>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3}>
        {/* Card 1: Processing Fee Mode */}
        <Card variant="outlined">
          <CardHeader title="Processing Fee Mode" />
          <CardContent>
            {isLoading ? (
              <Skeleton variant="rectangular" height={120} />
            ) : (
              <Controller
                name="feeMode"
                control={control}
                render={({ field }) => (
                  <FormControl component="fieldset">
                    <FormLabel component="legend">
                      How should Stripe processing fees be handled?
                    </FormLabel>
                    <RadioGroup {...field}>
                      <FormControlLabel
                        value="ABSORB"
                        control={<Radio />}
                        label={
                          <Box>
                            <Typography variant="body1">Absorb fees (default)</Typography>
                            <Typography variant="body2" color="text.secondary">
                              The school pays Stripe processing fees. Customers see only the listed price.
                            </Typography>
                          </Box>
                        }
                      />
                      <FormControlLabel
                        value="PASS_TO_CUSTOMER"
                        control={<Radio />}
                        label={
                          <Box>
                            <Typography variant="body1">Pass fees to customer</Typography>
                            <Typography variant="body2" color="text.secondary">
                              A processing fee line item is added at checkout. The customer pays the base price plus the fee.
                            </Typography>
                          </Box>
                        }
                      />
                    </RadioGroup>
                  </FormControl>
                )}
              />
            )}
          </CardContent>
        </Card>

        {/* Card 2: Fee Configuration (only when PASS_TO_CUSTOMER) */}
        {feeMode === 'PASS_TO_CUSTOMER' && (
          <Card variant="outlined">
            <CardHeader title="Fee Configuration" />
            <CardContent>
              <Stack spacing={3}>
                {isLoading ? (
                  <Skeleton variant="rectangular" height={200} />
                ) : (
                  <>
                    {/* Country selector */}
                    <Controller
                      name="countryCode"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth>
                          <InputLabel id="country-code-label">Country (determines default Stripe rate)</InputLabel>
                          <Select {...field} size="small" labelId="country-code-label" label="Country (determines default Stripe rate)">
                            {Object.entries(processingRates).map(([code, rate]) => (
                              <MenuItem key={code} value={code}>
                                {rate.label} ({rate.percentRate}% + {rate.fixedCents}c {rate.currency})
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    />

                    {/* Default rate display */}
                    {!useCustomRate && processingRates[countryCode] && (
                      <Alert severity="info" variant="outlined">
                        Default rate for {processingRates[countryCode].label}:{' '}
                        <strong>
                          {processingRates[countryCode].percentRate}% + {processingRates[countryCode].fixedCents}c
                        </strong>{' '}
                        per transaction
                      </Alert>
                    )}

                    {/* Custom rate toggle */}
                    <Controller
                      name="useCustomRate"
                      control={control}
                      render={({ field: { value, onChange, ...rest } }) => (
                        <FormControlLabel
                          control={
                            <Switch
                              checked={value}
                              onChange={(e) => onChange(e.target.checked)}
                              {...rest}
                            />
                          }
                          label="Use custom rate (override country default)"
                        />
                      )}
                    />

                    {/* Custom rate fields */}
                    {useCustomRate && (
                      <Stack direction="row" spacing={2}>
                        <Controller
                          name="customPercentRate"
                          control={control}
                          rules={{
                            min: { value: 0, message: 'Cannot be negative' },
                            max: { value: 10, message: 'Cannot exceed 10%' },
                          }}
                          render={({ field, fieldState: { error: fieldError } }) => (
                            <TextField
                              {...field}
                              onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                              label="Percentage rate"
                              type="number"
                              size="small"
                              InputProps={{
                                endAdornment: <InputAdornment position="end">%</InputAdornment>,
                              }}
                              inputProps={{ step: 0.01, min: 0, max: 10 }}
                              error={!!fieldError}
                              helperText={fieldError?.message}
                              sx={{ width: 180 }}
                            />
                          )}
                        />
                        <Controller
                          name="customFixedCents"
                          control={control}
                          rules={{
                            min: { value: 0, message: 'Cannot be negative' },
                            max: { value: 100, message: 'Cannot exceed 100c' },
                          }}
                          render={({ field, fieldState: { error: fieldError } }) => (
                            <TextField
                              {...field}
                              onChange={(e) => field.onChange(parseInt(e.target.value, 10) || 0)}
                              label="Fixed amount"
                              type="number"
                              size="small"
                              InputProps={{
                                endAdornment: <InputAdornment position="end">cents</InputAdornment>,
                              }}
                              inputProps={{ step: 1, min: 0, max: 100 }}
                              error={!!fieldError}
                              helperText={fieldError?.message}
                              sx={{ width: 180 }}
                            />
                          )}
                        />
                      </Stack>
                    )}

                    <Alert severity="info" variant="outlined">
                      Check your Stripe Dashboard (Settings &gt; Pricing) for your actual rate.
                      Negotiated rates may differ from the defaults shown here.
                    </Alert>
                  </>
                )}
              </Stack>
            </CardContent>
          </Card>
        )}

        {/* Card 3: Fee Preview (only when PASS_TO_CUSTOMER) */}
        {feeMode === 'PASS_TO_CUSTOMER' && (
          <Card variant="outlined">
            <CardHeader title="Fee Preview" />
            <CardContent>
              <Stack spacing={1}>
                <Typography variant="body2" color="text.secondary">
                  Example calculation for a $100.00 payment:
                </Typography>
                <Stack direction="row" justifyContent="space-between" sx={{ maxWidth: 300 }}>
                  <Typography>Base amount:</Typography>
                  <Typography>${preview.base.toFixed(2)}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between" sx={{ maxWidth: 300 }}>
                  <Typography>Processing fee:</Typography>
                  <Typography>${preview.fee.toFixed(2)}</Typography>
                </Stack>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  sx={{ maxWidth: 300, borderTop: 1, borderColor: 'divider', pt: 1 }}
                >
                  <Typography fontWeight="bold">Customer pays:</Typography>
                  <Typography fontWeight="bold">${preview.total.toFixed(2)}</Typography>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        )}

        {/* Warning */}
        <Alert severity="warning">
          Changes only apply to new payments and subscriptions.
          Existing subscriptions will not be affected.
        </Alert>

        {/* Action Buttons */}
        {canSave && (
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button
              type="submit"
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={!isDirty || updateMutation.isPending}
            >
              {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
            <Button
              variant="outlined"
              startIcon={<ResetIcon />}
              onClick={() => setResetDialogOpen(true)}
              disabled={resetMutation.isPending}
            >
              {resetMutation.isPending ? 'Resetting...' : 'Reset to Defaults'}
            </Button>
          </Stack>
        )}
      </Stack>

      {/* Reset Confirmation Dialog */}
      <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)}>
        <DialogTitle>Reset Payment Settings?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will reset all payment settings to their defaults (Absorb fees, Australia rate).
            New payments will no longer include a processing fee line item.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleResetConfirm} color="error" variant="contained">
            Reset
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
